import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Check, X,
  Stethoscope, Brain, Pill, FileText, Heartbeat, CalendarCheck,
  Pulse, ChatCircleDots, Eye, ShieldCheck,
  UserCircle, TreeStructure, FirstAidKit,
  ChartLineUp, Lightning, Notepad,
  Lock, Database, UsersFour, Globe,
  Rocket, Handshake, Warning, TrendDown,
  CurrencyCircleDollar, ArrowsClockwise, Users,
  Repeat, Heart, ListChecks, Gear,
  CaretRight, CircleNotch
} from '@phosphor-icons/react';

/* ─── animation primitives ─── */
const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }} className={className}>
      {children}
    </motion.div>
  );
};

const SL = ({ children, light }) => (
  <p className={`text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${light ? 'text-emerald-400' : 'text-[#235347]'}`}>{children}</p>
);
const H2 = ({ children, light, className = '' }) => (
  <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight leading-[1.15] ${light ? 'text-white' : 'text-[#0F172A]'} ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h2>
);

/* ═══════════════════════════════════════════════════
   VISUAL 1 — HERO INFOGRAPHIC
   fragmented healthcare vs connected Kindra layer
   ═══════════════════════════════════════════════════ */
const HeroInfographic = () => {
  const fragments = [
    { icon: Stethoscope, label: 'GP visit', col: 'col-start-1 row-start-1' },
    { icon: Brain, label: 'Specialist', col: 'col-start-3 row-start-1' },
    { icon: Pill, label: 'Pharmacy', col: 'col-start-2 row-start-2' },
    { icon: FileText, label: 'Lab results', col: 'col-start-1 row-start-3' },
    { icon: Heartbeat, label: 'Symptoms', col: 'col-start-3 row-start-3' },
    { icon: CalendarCheck, label: 'Next appt', col: 'col-start-2 row-start-4' },
  ];

  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Fragmented nodes */}
      <div className="grid grid-cols-3 grid-rows-4 gap-3" style={{ minHeight: 280 }}>
        {fragments.map(({ icon: Icon, label, col }, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
            className={`${col} bg-white/[0.06] border border-white/10 rounded-lg p-3 flex flex-col items-center justify-center gap-1 ${i % 2 === 0 ? 'opacity-40' : 'opacity-55'}`}
          >
            <Icon size={18} className="text-slate-400" weight="duotone" />
            <p className="text-[9px] text-slate-500 text-center leading-tight">{label}</p>
          </motion.div>
        ))}
      </div>

      {/* Broken dashed lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 320 300" fill="none">
        <line x1="80" y1="50" x2="160" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 8" opacity="0.25" />
        <line x1="240" y1="50" x2="160" y2="100" stroke="#334155" strokeWidth="1" strokeDasharray="4 8" opacity="0.25" />
        <line x1="80" y1="200" x2="160" y2="150" stroke="#334155" strokeWidth="1" strokeDasharray="4 8" opacity="0.2" />
        <line x1="240" y1="200" x2="160" y2="150" stroke="#334155" strokeWidth="1" strokeDasharray="4 8" opacity="0.2" />
      </svg>

      {/* Gap badge */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
          <span className="text-[10px] text-red-400 font-medium tracking-wide">No system connects this</span>
        </div>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   VISUAL 2 — SYSTEM DIAGRAM
   user → data → Kindra → insights → doctor
   ═══════════════════════════════════════════════════ */
const SystemDiagram = () => {
  const steps = [
    { icon: UserCircle, label: 'User', sub: 'daily life', color: 'bg-slate-700' },
    { icon: Database, label: 'Data', sub: 'check-ins, docs, chat', color: 'bg-slate-600' },
    { icon: Brain, label: 'Kindra', sub: 'intelligence engine', color: 'bg-[#235347]' },
    { icon: Eye, label: 'Insights', sub: 'patterns, alerts', color: 'bg-slate-600' },
    { icon: Stethoscope, label: 'Doctor', sub: 'structured brief', color: 'bg-slate-700' },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-0">
      {steps.map(({ icon: Icon, label, sub, color }, i) => (
        <div key={i} className="flex items-center">
          <FadeUp delay={0.1 * i}>
            <div className={`${color} rounded-xl p-4 sm:p-5 text-center min-w-[100px]`}>
              <Icon size={24} className="text-white mx-auto mb-1.5" weight="duotone" />
              <p className="text-xs font-semibold text-white">{label}</p>
              <p className="text-[9px] text-white/50 mt-0.5">{sub}</p>
            </div>
          </FadeUp>
          {i < steps.length - 1 && (
            <CaretRight size={16} className="text-slate-500 mx-1 hidden sm:block flex-shrink-0" />
          )}
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   VISUAL 5 — VALUE LOOP DIAGRAM
   data → insight → better care → more data → stronger
   ═══════════════════════════════════════════════════ */
const ValueLoop = () => {
  const nodes = [
    { icon: Pulse, label: 'Data captured', sub: 'Check-ins, symptoms, documents' },
    { icon: Eye, label: 'Insights emerge', sub: 'Patterns, correlations, alerts' },
    { icon: ShieldCheck, label: 'Better care', sub: 'Prepared visits, informed decisions' },
    { icon: ArrowsClockwise, label: 'More engagement', sub: 'User returns because it works' },
  ];

  return (
    <div className="relative max-w-lg mx-auto">
      <div className="grid grid-cols-2 gap-5">
        {nodes.map(({ icon: Icon, label, sub }, i) => (
          <FadeUp key={i} delay={0.08 * i}>
            <div className="bg-white border border-slate-200 rounded-xl p-5 text-center relative">
              <div className="w-10 h-10 rounded-full bg-[#235347]/10 flex items-center justify-center mx-auto mb-3">
                <Icon size={20} className="text-[#235347]" weight="duotone" />
              </div>
              <p className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{label}</p>
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{sub}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      {/* Loop arrows - CSS based */}
      <div className="absolute inset-0 pointer-events-none hidden sm:block">
        <svg viewBox="0 0 400 320" fill="none" className="w-full h-full">
          <path d="M200 10 C 320 10, 390 80, 390 160 C 390 240, 320 310, 200 310 C 80 310, 10 240, 10 160 C 10 80, 80 10, 200 10" stroke="#235347" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.2" fill="none" />
          {/* Arrowhead */}
          <polygon points="210,8 200,2 200,14" fill="#235347" opacity="0.3" />
        </svg>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════ */
const InvestorPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#F8F8F8] text-[#0F172A] overflow-x-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ═══ NAV ═══ */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200" data-testid="investor-nav" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="investor-nav-logo">
            <img src="/kindra-logo.png" alt="Kindra" className="h-7 object-contain" />
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="investor-nav-login" onClick={() => navigate('/login')} className="text-sm text-slate-600 hover:text-[#235347] transition-colors font-medium">
              Sign in
            </button>
            <a data-testid="investor-nav-cta" href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Investor%20Inquiry" className="bg-[#235347] text-white text-sm px-5 py-2 rounded-md font-medium hover:bg-[#1A3E35] transition-colors">
              Get in touch
            </a>
          </div>
        </div>
      </nav>


      {/* ═══ 1. HERO ═══ */}
      <section className="bg-[#0C1220] text-white overflow-hidden" data-testid="investor-hero">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <FadeUp>
                <SL light>Personal health intelligence</SL>
              </FadeUp>
              <FadeUp delay={0.08}>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.08]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Healthcare stops between appointments.<br />
                  <span className="text-emerald-400">Kindra doesn't.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.16}>
                <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-lg">
                  The support layer that exists between visits — capturing what changes, connecting the dots, and making sure nothing is lost.
                </p>
                <p className="mt-3 text-sm text-slate-500">
                  Your doctor sees you for minutes. Kindra supports you the rest of the time.
                </p>
              </FadeUp>
              <FadeUp delay={0.24}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button data-testid="hero-cta-explore" onClick={() => document.getElementById('the-problem')?.scrollIntoView({ behavior: 'smooth' })} className="bg-[#235347] text-white px-6 py-3 rounded-md font-medium hover:bg-[#1A3E35] transition-colors flex items-center gap-2 text-sm">
                    Explore the platform <ArrowRight size={16} />
                  </button>
                  <a data-testid="hero-cta-access" href="/register" className="border border-white/20 text-slate-300 px-6 py-3 rounded-md font-medium hover:bg-white/5 transition-colors text-sm">
                    Start free trial
                  </a>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={0.3}>
              <HeroInfographic />
            </FadeUp>
          </div>
        </div>
      </section>


      {/* ═══ 2. THE PROBLEM ═══ */}
      <section id="the-problem" className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="investor-problem">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <FadeUp>
                <SL>The problem</SL>
                <H2>Healthcare is episodic.<br />Life is continuous.</H2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  You see your doctor for 15 minutes every few months. In between — symptoms change, medications affect you differently, sleep deteriorates, something feels off. By the time you're back in the room, you've forgotten half of it.
                </p>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  Patients are expected to manage their own health with no infrastructure, no continuity, and no support. The system works in appointments. Life doesn't.
                </p>
              </FadeUp>
            </div>

            {/* Fragmented system visual */}
            <FadeUp delay={0.15}>
              <div className="space-y-2.5">
                {[
                  { icon: TrendDown, text: 'Symptoms change but aren\'t recorded', color: 'text-amber-500' },
                  { icon: Pill, text: 'Medications shift with no tracking of effects', color: 'text-blue-500' },
                  { icon: Warning, text: 'Important details are forgotten before visits', color: 'text-red-500' },
                  { icon: CircleNotch, text: 'No system connects conditions, symptoms, and medications', color: 'text-slate-400' },
                ].map(({ icon: Icon, text, color }, i) => (
                  <FadeUp key={i} delay={0.1 + i * 0.06}>
                    <div className="bg-[#FAFAFA] border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                      <Icon size={18} className={`${color} mt-0.5 flex-shrink-0`} weight="duotone" />
                      <p className="text-sm text-slate-600">{text}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>


      {/* ═══ 3. THE GAP ═══ */}
      <section className="bg-[#0C1220] text-white py-16 md:py-24" data-testid="investor-gap">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>The gap</SL>
            <H2 light className="max-w-3xl">There is no system that does what patients actually need.</H2>
          </FadeUp>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'Continuously tracks real-life health between visits',
              'Connects conditions, symptoms, and medications together',
              'Prepares patients with structured data before care',
              'Supports them in the months between appointments',
            ].map((text, i) => (
              <FadeUp key={i} delay={0.06 * i}>
                <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02] h-full flex items-start gap-3">
                  <X size={14} className="text-red-400 mt-0.5 flex-shrink-0" weight="bold" />
                  <p className="text-xs text-slate-400 leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.3}>
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-5 py-2">
                <Check size={14} className="text-emerald-400" weight="bold" />
                <span className="text-sm text-emerald-400 font-medium">Kindra fills every one of these gaps.</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ═══ 4. THE KINDRA SOLUTION ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="investor-solution">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>The solution</SL>
            <H2 className="max-w-3xl">Kindra is a personal health intelligence companion.</H2>
            <p className="mt-4 text-sm text-slate-500 max-w-xl leading-relaxed">
              A system that lives between appointments — tracking, connecting, and preparing. Seven integrated capabilities, one continuous layer.
            </p>
          </FadeUp>

          {/* System diagram */}
          <FadeUp delay={0.12}>
            <div className="mt-14 mb-16">
              <SystemDiagram />
            </div>
          </FadeUp>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Pulse, title: 'Continuous tracking', desc: 'Mood, sleep, energy, symptoms — captured daily, not recalled weeks later.' },
              { icon: TreeStructure, title: 'Structured record', desc: 'Conditions, medications, events — connected in one persistent health profile.' },
              { icon: Eye, title: 'Pattern detection', desc: 'Slow changes invisible day-to-day become clear over time.' },
              { icon: CalendarCheck, title: 'Appointment prep', desc: 'Structured brief before every visit — what changed, what to ask.' },
              { icon: FileText, title: 'Document understanding', desc: 'Reports, scans, letters — uploaded, read, referenced. Searchable history.' },
              { icon: Brain, title: 'Cross-condition awareness', desc: 'How conditions interact, symptoms overlap, medications influence each other.' },
              { icon: ChatCircleDots, title: 'Always-available support', desc: 'Talk to Kindra anytime. It remembers last week, last month, and why it matters.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={0.05 * i}>
                <div className={`bg-[#FAFAFA] border border-slate-200 rounded-lg p-5 h-full ${i === 6 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                  <Icon size={22} className="text-[#235347] mb-3" weight="duotone" />
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ 5. THE RARE ADVANTAGE ═══ */}
      <section className="py-20 md:py-28" data-testid="investor-advantage">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>The rare advantage</SL>
            <H2 className="max-w-3xl">A system that works for everyone — by adapting to the individual.</H2>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            <FadeUp delay={0.06}>
              <div className="border-l-2 border-[#235347] pl-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Not niche-locked</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Most health apps target one condition, one demographic, or one use case. Kindra adapts to each user's complexity — whether they manage one condition or seven.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="border-l-2 border-[#235347] pl-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Mental + physical</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Health doesn't separate into categories. Anxiety affects sleep. Sleep affects pain. Kindra tracks across both mental and physical health because that's how real patients live.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.18}>
              <div className="border-l-2 border-[#235347] pl-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Evolves over time</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Day one, Kindra is useful. Month three, it understands patterns. Year one, it knows more about your health trajectory than any single provider. The system compounds.
                </p>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.24}>
            <div className="mt-12 bg-[#0C1220] text-white rounded-xl p-6 md:p-8 max-w-2xl">
              <p className="text-base font-medium leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>
                "Kindra is not one experience. It becomes a different system for every user — shaped by their conditions, their history, and their life."
              </p>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ═══ 6. HOW IT WORKS ═══ */}
      <section className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="investor-how">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>How it works</SL>
            <H2>Five steps. Then it gets smarter every day.</H2>
          </FadeUp>

          {/* Flow diagram */}
          <div className="mt-14">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { n: '01', icon: UserCircle, t: 'Share your health story', d: 'Conditions, medications, symptoms, medical history — guided onboarding captures it all.' },
                { n: '02', icon: TreeStructure, t: 'Structured profile built', d: 'A real health record with cross-condition awareness and medication interactions.' },
                { n: '03', icon: FirstAidKit, t: 'Everything connects', d: 'Documents, check-ins, conversations — one continuous layer that accumulates.' },
                { n: '04', icon: Eye, t: 'Patterns surface', d: 'What\'s changing between visits. What correlates. What to watch.' },
                { n: '05', icon: ShieldCheck, t: 'Walk in prepared', d: 'Structured brief. Full context. Every appointment. Nothing forgotten.' },
              ].map(({ n, icon: Icon, t, d }, i) => (
                <FadeUp key={n} delay={0.06 * i}>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-[#235347]/10 flex items-center justify-center mb-3">
                      <Icon size={20} className="text-[#235347]" weight="duotone" />
                    </div>
                    <span className="text-[10px] font-bold text-[#235347] tracking-[0.2em] block mb-0.5">{n}</span>
                    <h3 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{d}</p>
                    {/* Connector line */}
                    {i < 4 && (
                      <div className="hidden md:block absolute top-5 -right-3 w-6">
                        <div className="w-full h-px bg-slate-300" />
                        <CaretRight size={10} className="text-slate-300 absolute -right-1 -top-[4px]" />
                      </div>
                    )}
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ═══ 7. REAL-WORLD IMPACT ═══ */}
      <section className="bg-[#235347] text-white py-20 md:py-28" data-testid="investor-impact">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>Real-world impact</SL>
            <H2 light className="max-w-2xl">What changes when there's continuity between appointments.</H2>
          </FadeUp>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: CalendarCheck, title: 'Better appointments', desc: 'Your doctor gets a structured brief instead of "How have you been?" — "Um, fine."' },
              { icon: Heart, title: 'Less overwhelm', desc: 'Stop carrying everything in your head. One system holds it, connects it, and reminds you.' },
              { icon: ListChecks, title: 'Clearer thinking', desc: 'Organised health picture means better decisions, better questions, better outcomes.' },
              { icon: ShieldCheck, title: 'More confidence', desc: 'Knowing your health is tracked reduces the anxiety of forgetting something important.' },
              { icon: ChatCircleDots, title: 'Support between visits', desc: 'Kindra stays present in the months between appointments. You\'re not managing alone.' },
              { icon: Lock, title: 'A record that follows you', desc: 'Not locked in one portal. Your health story — continuous, structured, and yours.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={0.05 * i}>
                <div className="bg-white/10 border border-white/10 rounded-lg p-5 h-full">
                  <Icon size={20} className="text-emerald-300 mb-2.5" weight="duotone" />
                  <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-emerald-100/60 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ 8. DIFFERENTIATION — COMPARISON TABLE ═══ */}
      <section className="bg-[#0C1220] text-white py-20 md:py-28" data-testid="investor-comparison">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>Differentiation</SL>
            <H2 light>Not a tracker. Not a portal. Not a chatbot.</H2>
            <p className="mt-3 text-slate-400 max-w-md text-sm">A persistent intelligence layer that exists between appointments. It remembers. It connects. It prepares.</p>
          </FadeUp>

          <FadeUp delay={0.12}>
            <div className="mt-10 overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[640px] text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 pr-4 text-slate-500 text-xs font-medium w-[200px]">Capability</th>
                    <th className="py-3 px-3 text-slate-600 text-xs font-medium text-center">Notes</th>
                    <th className="py-3 px-3 text-slate-600 text-xs font-medium text-center">Trackers</th>
                    <th className="py-3 px-3 text-slate-600 text-xs font-medium text-center">Portals</th>
                    <th className="py-3 px-3 text-slate-600 text-xs font-medium text-center">AI Chat</th>
                    <th className="py-3 px-3 text-emerald-400 text-xs font-semibold text-center bg-white/[0.03] rounded-t-lg">Kindra</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Continuous health memory', 0, 0, 0, 0, 1],
                    ['Structured health record', 0, 0, 1, 0, 1],
                    ['Pattern detection over time', 0, 1, 0, 0, 1],
                    ['Cross-condition awareness', 0, 0, 0, 0, 1],
                    ['Document understanding', 0, 0, 0, 0, 1],
                    ['Appointment preparation', 0, 0, 0, 0, 1],
                    ['Continuous presence between visits', 0, 0, 0, 0, 1],
                  ].map(([label, ...vals], i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2.5 pr-4 text-xs text-slate-300">{label}</td>
                      {vals.map((v, j) => (
                        <td key={j} className={`py-2.5 px-3 text-center ${j === 4 ? 'bg-white/[0.03]' : ''}`}>
                          {v ? <Check size={14} weight="bold" className={`inline ${j === 4 ? 'text-emerald-400' : 'text-slate-500'}`} /> : <X size={14} className="inline text-slate-700" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ═══ 9. BUILT FOR REAL COMPLEXITY ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="investor-complexity">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <FadeUp>
                <SL>Built for real complexity</SL>
                <H2>This is not a wellness app.<br />This is a health intelligence system.</H2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  Most health apps are designed for healthy people trying to optimise. Kindra is designed for people living with real health complexity — where the stakes are higher, the system is harder to navigate, and the gaps between appointments are where things fall apart.
                </p>
              </FadeUp>
            </div>

            <FadeUp delay={0.15}>
              <div className="space-y-3">
                {[
                  'Multiple conditions managed simultaneously',
                  'Medication changes tracked with effects over time',
                  'Overlapping symptoms across conditions',
                  'Life context that affects health outcomes',
                  'Complex care teams with fragmented records',
                  'Long-term trajectory that no single provider sees',
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#FAFAFA] border border-slate-200 rounded-lg p-3.5">
                    <Check size={14} className="text-[#235347] mt-0.5 flex-shrink-0" weight="bold" />
                    <p className="text-xs text-slate-600">{text}</p>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>


      {/* ═══ 10. EMOTIONAL LAYER ═══ */}
      <section className="py-20 md:py-24" data-testid="investor-emotional">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL>The human truth</SL>
            <H2 className="max-w-2xl">People don't just need better tools. They need to feel less alone.</H2>
          </FadeUp>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {[
              { title: 'Overwhelmed', desc: 'Managing health is a full-time job no one trained you for. Multiple conditions, medications, appointments — and you\'re expected to hold it all together.', highlight: 'Kindra gives you structure.' },
              { title: 'Alone between visits', desc: 'Your doctor is available for 15 minutes. The other 3 months? You\'re on your own. Symptoms change, concerns build, but there\'s no one to tell.', highlight: 'Kindra stays present.' },
              { title: 'Constantly repeating', desc: 'Every new provider, every appointment: "Tell me your history." Again. From scratch. Nothing carries over. Nothing connects.', highlight: 'Kindra remembers everything.' },
            ].map(({ title, desc, highlight }, i) => (
              <FadeUp key={i} delay={0.06 * i}>
                <div className="h-full">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-4">{desc}</p>
                  <div className="bg-[#235347] text-white rounded-lg px-4 py-2.5">
                    <p className="text-xs font-medium">{highlight}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.24}>
            <div className="mt-14 max-w-2xl">
              <p className="text-base text-slate-700 font-medium leading-relaxed italic border-l-2 border-[#235347] pl-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                "A system that doesn't disappear when you leave the clinic."
              </p>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ═══ 11. MARKET + BUSINESS OPPORTUNITY ═══ */}
      <section className="bg-[#0C1220] text-white py-20 md:py-28" data-testid="investor-market">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp>
            <SL light>Market opportunity</SL>
            <H2 light className="max-w-3xl">The biggest gap in healthcare is the lack of continuity. Kindra fills it.</H2>
          </FadeUp>

          {/* Value loop */}
          <FadeUp delay={0.1}>
            <div className="mt-14 mb-16">
              <p className="text-xs text-slate-500 text-center mb-6 tracking-wider uppercase font-semibold">The compounding value loop</p>
              <ValueLoop />
            </div>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Why now */}
            <FadeUp delay={0.08}>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-6 h-full">
                <Lightning size={20} className="text-emerald-400 mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Why now</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Chronic conditions are the #1 global health burden — and rising</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Care is more fragmented than ever across providers and systems</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />AI enables continuous, personalised health intelligence for the first time</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Patients are expected to self-manage with no tools built for them</li>
                </ul>
              </div>
            </FadeUp>

            {/* Retention */}
            <FadeUp delay={0.14}>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-6 h-full">
                <Database size={20} className="text-emerald-400 mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Natural retention</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Health data compounds — every day Kindra becomes more valuable</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Users return because it holds information they need — not gamification</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Switching cost is real — years of structured health history</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />After 3 months, Kindra understands patterns no other tool could</li>
                </ul>
              </div>
            </FadeUp>

            {/* Revenue */}
            <FadeUp delay={0.2}>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-6 h-full">
                <CurrencyCircleDollar size={20} className="text-emerald-400 mb-3" weight="duotone" />
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue channels</h3>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Direct consumer subscription (core intelligence)</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Premium insights and advanced pattern detection</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Family and caregiver collaboration plans</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Clinical integration — patient data between visits</li>
                  <li className="flex items-start gap-2"><div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />Insurer and health system partnerships (future)</li>
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>


      {/* ═══ 11b. MARKET SIZE & COMMERCIAL OPPORTUNITY ═══ */}
      <section className="bg-white border-b border-slate-200 py-20 md:py-28" data-testid="investor-commercial">
        <div className="max-w-6xl mx-auto px-6">

          {/* Header */}
          <FadeUp>
            <SL>Market size &amp; commercial opportunity</SL>
            <H2 className="max-w-3xl">The scale of the problem — and the opportunity inside it.</H2>
          </FadeUp>

          {/* 1. Stat grid */}
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { stat: '1 in 2+', label: 'adults live with at least one chronic condition' },
              { stat: 'Multi-condition', label: 'care is rapidly increasing — managing 2+ conditions is becoming the norm' },
              { stat: 'Mental health', label: 'demand exceeds supply globally — wait times extend from weeks to months' },
              { stat: 'Between visits', label: 'no system supports patients in the months between appointments' },
            ].map(({ stat, label }, i) => (
              <FadeUp key={i} delay={0.05 * i}>
                <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-5 h-full">
                  <p className="text-xl font-bold text-[#235347] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{label}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.24}>
            <p className="mt-6 text-sm text-slate-600 max-w-2xl leading-relaxed border-l-2 border-[#235347] pl-5">
              The need for continuous, between-appointment support is not niche — it is widespread and growing.
            </p>
          </FadeUp>


          {/* 2. Addressable market */}
          <FadeUp delay={0.08}>
            <div className="mt-16">
              <h3 className="text-base font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Where Kindra sits</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  'Chronic condition management',
                  'Mental health support',
                  'Patient engagement',
                  'Digital health infrastructure',
                ].map((text, i) => (
                  <div key={i} className="bg-[#0C1220] text-white rounded-lg px-4 py-3 text-center">
                    <p className="text-xs font-medium">{text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-slate-500 leading-relaxed max-w-2xl">
                This positions Kindra within a multi-billion-dollar global health technology market, without being limited to a single condition, specialty, or user segment.
              </p>
            </div>
          </FadeUp>


          {/* 3. Revenue model */}
          <FadeUp delay={0.1}>
            <div className="mt-16">
              <h3 className="text-base font-semibold text-[#0F172A] mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue model</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: 'Subscription', desc: 'Monthly recurring access to the core platform and intelligence layer.' },
                  { title: 'Premium intelligence', desc: 'Advanced insights, enhanced reports, and deeper pattern analysis.' },
                  { title: 'Caregiver / family plans', desc: 'Shared access for support networks — partners, parents, carers.' },
                  { title: 'Clinical tools (future)', desc: 'Patient summaries and between-visit visibility for allied health.' },
                  { title: 'Health system partnerships', desc: 'Improved outcomes through better data continuity at scale.' },
                ].map(({ title, desc }, i) => (
                  <div key={i} className={`border border-slate-200 rounded-lg p-4 ${i === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>


          {/* 4. Revenue scenario */}
          <FadeUp delay={0.12}>
            <div className="mt-16 bg-[#0C1220] rounded-xl p-6 md:p-8">
              <h3 className="text-sm font-semibold text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Illustrative revenue scenarios</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { users: '10,000', monthly: '~$150K', annual: '~$1.8M' },
                  { users: '50,000', monthly: '~$750K', annual: '~$9M' },
                  { users: '100,000', monthly: '~$1.5M', annual: '~$18M' },
                ].map(({ users, monthly, annual }, i) => (
                  <div key={i} className="bg-white/[0.05] border border-white/10 rounded-lg p-5 text-center">
                    <p className="text-2xl font-bold text-emerald-400 mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{annual}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">per year</p>
                    <div className="border-t border-white/10 pt-3 space-y-1">
                      <p className="text-xs text-slate-300"><span className="text-white font-medium">{users}</span> users</p>
                      <p className="text-[11px] text-slate-500">{monthly}/month at $15/user</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
                Based on modest pricing and user adoption — not aggressive projections. Scenarios shown for context only.
              </p>
            </div>
          </FadeUp>


          {/* 5 + 6. Retention + Defensibility — side by side */}
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {/* 5. Retention */}
            <FadeUp delay={0.06}>
              <div className="h-full">
                <h3 className="text-base font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Why retention is strong</h3>
                <div className="space-y-2.5">
                  {[
                    'Data accumulates — the system becomes more valuable the longer it is used',
                    'Users build reliance on memory, history, and insights',
                    'Switching cost increases over time — years of structured health data',
                    'Engagement is driven by real utility, not gamification or notifications',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2.5 bg-[#FAFAFA] border border-slate-200 rounded-lg p-3">
                      <Check size={13} className="text-[#235347] mt-0.5 flex-shrink-0" weight="bold" />
                      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-700 font-medium italic border-l-2 border-[#235347] pl-4">
                  Kindra is not just used — it is built up over time.
                </p>
              </div>
            </FadeUp>

            {/* 6. Defensibility */}
            <FadeUp delay={0.12}>
              <div className="h-full">
                <h3 className="text-base font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Why this is defensible</h3>
                <div className="space-y-2.5">
                  {[
                    'Structured health memory that compounds with every interaction',
                    'Cross-condition intelligence connecting symptoms, medications, and events',
                    'Behaviour-driven data capture embedded in daily use',
                    'Compounding personalisation that deepens over months and years',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2.5 bg-[#FAFAFA] border border-slate-200 rounded-lg p-3">
                      <Lock size={13} className="text-[#235347] mt-0.5 flex-shrink-0" weight="bold" />
                      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-700 font-medium italic border-l-2 border-[#235347] pl-4">
                  This is not easily replicated by simple apps or generic AI tools.
                </p>
              </div>
            </FadeUp>
          </div>

        </div>
      </section>


      {/* ═══ 12. VISION ═══ */}
      <section className="bg-white border-y border-slate-200 py-20 md:py-24" data-testid="investor-vision">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <FadeUp>
            <SL>The vision</SL>
            <H2 className="max-w-2xl mx-auto">Healthcare is episodic. Life is continuous.<br />Kindra bridges the gap.</H2>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { icon: Brain, label: 'Deeper condition intelligence' },
                { icon: FileText, label: 'Document-driven memory' },
                { icon: Handshake, label: 'Caregiver collaboration' },
                { icon: Rocket, label: 'Voice and real-time support' },
              ].map(({ icon: Icon, label }, i) => (
                <FadeUp key={i} delay={0.05 * i}>
                  <div className="bg-[#FAFAFA] border border-slate-200 rounded-lg p-5">
                    <Icon size={22} className="text-[#235347] mx-auto mb-2" weight="duotone" />
                    <p className="text-xs text-slate-600 font-medium">{label}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ═══ 13. FINAL CTA ═══ */}
      <section className="bg-[#0C1220] py-28 md:py-36" data-testid="investor-cta">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <FadeUp>
            <p className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-6">The opportunity</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] max-w-3xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The support layer that exists between appointments.<br />
              That's what Kindra builds.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 text-slate-500 max-w-md mx-auto text-sm leading-relaxed">
              A serious product solving a serious gap in healthcare. Built by someone who understands the problem from the inside.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <a data-testid="final-cta-access" href="/register" className="bg-[#235347] text-white px-8 py-4 rounded-md font-medium hover:bg-[#1A3E35] transition-colors flex items-center gap-2 text-sm">
                Start your free trial <ArrowUpRight size={16} />
              </a>
              <a data-testid="final-cta-founder" href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Investor%20Inquiry" className="border border-slate-600 text-slate-400 px-8 py-4 rounded-md font-medium hover:bg-white/5 transition-colors text-sm">
                Talk to the founder
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-16 opacity-30">
              <img src="/kindra-logo.png" alt="" className="h-5 mx-auto object-contain" />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default InvestorPage;
